import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MoneyFlowStepper, type MoneyFlowStep } from "../components/MoneyFlowStepper";
import {
  approvePartnerApplication,
  createPartnerApplicationProposal,
  getPartnerApplication,
  rejectPartnerApplication,
  suspendPartnerApplication,
  type PartnerApplicationRow,
  type PartnerProposalAction,
} from "../api/partnerApplications";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";

// Partner-application detail + three operator mutations.
//
// approve / reject / suspend are `low_config` on the BE: they flip
// identity_affiliate_index.partner_approved and (un)lock the partner-cash
// withdrawal flow. Each REQUIRES an approved multisign proposalId. The
// operator creates + approves a low_config proposal OUTSIDE this screen
// (the browser session cannot call the HMAC-authed propose endpoint, and
// we never put a secret in the browser), then pastes the approved
// proposalId here. reject / suspend also require a reason.

type Dialog = { kind: "none" } | { kind: "approve" } | { kind: "reject" } | { kind: "suspend" };

function PartnerApplicationDetailInner({ id }: { id: string }) {
  const [detail, setDetail] = useState<PartnerApplicationRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [proposalIdInput, setProposalIdInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");

  const [dialog, setDialog] = useState<Dialog>({ kind: "none" });
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [refetchWarning, setRefetchWarning] = useState<string | null>(null);

  const [creatingProposal, setCreatingProposal] = useState<PartnerProposalAction | null>(null);
  const [createdProposalId, setCreatedProposalId] = useState<string | null>(null);
  const [createProposalError, setCreateProposalError] = useState<string | null>(null);

  // Create the low_config proposal server-side (browser session, no HMAC),
  // then auto-fill the proposalId input. The operator still approves the
  // proposal in /approvals before the matching mutation can use it.
  async function handleCreateProposal(action: PartnerProposalAction) {
    if (!detail) return;
    const reason = reasonInput.trim();
    if ((action === "reject" || action === "suspend") && reason.length === 0) {
      setCreateProposalError(`Enter a reason before creating a ${action} proposal.`);
      return;
    }
    setCreatingProposal(action);
    setCreateProposalError(null);
    try {
      const res = await createPartnerApplicationProposal(detail.id, {
        action,
        ...(action === "approve" ? {} : { reason }),
      });
      setProposalIdInput(res.proposalId);
      setCreatedProposalId(res.proposalId);
      setSuccessNotice(
        `low_config ${action} proposal created (${res.proposalId}). Approve it in Approvals, then run the ${action} action below.`,
      );
    } catch (e) {
      setCreateProposalError(mapAffiliateError(e).message);
    } finally {
      setCreatingProposal(null);
    }
  }

  const load = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      setDetail(await getPartnerApplication(id));
    } catch (e) {
      setLoadError(mapAffiliateError(e).message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function refetchDetail(): Promise<boolean> {
    try {
      setDetail(await getPartnerApplication(id));
      return true;
    } catch {
      return false;
    }
  }

  function closeDialog() {
    if (busy) return;
    setDialog({ kind: "none" });
    setDialogError(null);
  }

  async function performMutation(successLabel: string, mutate: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setDialogError(null);
    try {
      await mutate();
    } catch (e) {
      setDialogError(mapAffiliateError(e).message);
      setBusy(false);
      return;
    }
    setDialog({ kind: "none" });
    setSuccessNotice(successLabel);
    setRefetchWarning(null);
    const ok = await refetchDetail();
    if (!ok) {
      setRefetchWarning(
        `${successLabel} The current row state could not be refreshed — please reload to see authoritative state.`,
      );
    }
    setBusy(false);
  }

  async function runApprove() {
    if (!detail) return;
    await performMutation("Partner application approved — partner_approved set.", () =>
      approvePartnerApplication(detail.id, { proposalId: proposalIdInput.trim() }),
    );
  }
  async function runReject() {
    if (!detail) return;
    await performMutation("Partner application rejected.", () =>
      rejectPartnerApplication(detail.id, {
        proposalId: proposalIdInput.trim(),
        reason: reasonInput.trim(),
      }),
    );
  }
  async function runSuspend() {
    if (!detail) return;
    await performMutation("Partner suspended — partner_approved cleared.", () =>
      suspendPartnerApplication(detail.id, {
        proposalId: proposalIdInput.trim(),
        reason: reasonInput.trim(),
      }),
    );
  }

  if (loading && !detail) {
    return <p style={{ padding: 12, color: "var(--fg-dim)" }}>Loading…</p>;
  }
  if (loadError && !detail) {
    return (
      <div style={{ padding: 12 }}>
        <ErrorBox text={loadError} />
        <p style={{ marginTop: 12 }}>
          <Link to="/admin/affiliate/partners" style={{ color: "var(--accent, #2c7be5)" }}>
            ← Back to applications
          </Link>
        </p>
      </div>
    );
  }
  if (!detail) return null;

  const proposalIdValid = proposalIdInput.trim().length > 0;
  const reasonValid = reasonInput.trim().length > 0;
  const canApprove = detail.status === "pending";
  const canReject = detail.status === "pending";
  const canSuspend = detail.status === "approved";
  const anyAction = canApprove || canReject || canSuspend;

  const steps: ReadonlyArray<MoneyFlowStep> = [
    {
      label: "Review application",
      hint: "No funds move — this gates the partner-cash flow.",
      state: "done",
    },
    {
      label: "Create low_config proposal",
      hint: "Browser session creates it; reason bound for reject/suspend.",
      state: anyAction ? (createdProposalId ? "done" : "current") : "blocked",
    },
    {
      label: "Approve proposal",
      hint: "Approve in Approvals to reach threshold.",
      state: createdProposalId ? "current" : "upcoming",
    },
    {
      label: "Execute approve/reject/suspend",
      hint: "Flips partner_approved.",
      state: proposalIdValid ? "current" : "upcoming",
    },
  ];

  return (
    <div>
      <p style={{ margin: "8px 0", fontSize: 12 }}>
        <Link to="/admin/affiliate/partners" style={{ color: "var(--accent, #2c7be5)" }}>
          ← All partner applications
        </Link>
      </p>

      <MoneyFlowStepper steps={steps} ariaLabel="Partner application steps" />

      {successNotice && (
        <div
          role="status"
          style={{
            padding: 10,
            border: "1px solid var(--ok, #0a9444)",
            background: "rgba(10, 148, 68, 0.06)",
            color: "var(--ok, #0a9444)",
            borderRadius: 4,
            fontSize: 13,
            margin: "8px 0",
          }}
        >
          ✓ {successNotice}
        </div>
      )}
      {refetchWarning && (
        <div
          role="alert"
          style={{
            padding: 10,
            border: "1px solid var(--warn, #b58900)",
            background: "rgba(251, 191, 36, 0.08)",
            color: "var(--warn, #b58900)",
            borderRadius: 4,
            fontSize: 13,
            margin: "8px 0",
          }}
        >
          {refetchWarning}
        </div>
      )}

      <Section title="Application">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            <Row k="id" v={<code>{detail.id}</code>} />
            <Row k="identityId" v={detail.identityId ? <code>{detail.identityId}</code> : "—"} />
            <Row
              k="applicantWallet"
              v={detail.applicantWalletAddress ? <code>{detail.applicantWalletAddress}</code> : "—"}
            />
            <Row k="status" v={<strong>{detail.status}</strong>} />
            <Row
              k="applicantNote"
              v={
                detail.applicantNote ? (
                  <span style={{ whiteSpace: "pre-wrap" }}>{detail.applicantNote}</span>
                ) : (
                  "—"
                )
              }
            />
            <Row
              k="reviewNote"
              v={
                detail.reviewNote ? (
                  <span style={{ whiteSpace: "pre-wrap" }}>{detail.reviewNote}</span>
                ) : (
                  "—"
                )
              }
            />
            <Row k="reviewedBy" v={detail.reviewedByOwnerId ? <code>{detail.reviewedByOwnerId}</code> : "—"} />
            <Row k="reviewedAt" v={detail.reviewedAt ? <code>{detail.reviewedAt}</code> : "—"} />
            <Row k="createdAt" v={detail.createdAt ? <code>{detail.createdAt}</code> : "—"} />
            <Row k="updatedAt" v={detail.updatedAt ? <code>{detail.updatedAt}</code> : "—"} />
          </tbody>
        </table>
      </Section>

      {anyAction && (
        <Section title="Multisign proposalId (low_config)">
          <p style={{ margin: "0 0 8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
            Approve / reject / suspend are <code>low_config</code> actions and
            require an approved multisign proposal. Step 1: create a{" "}
            <code>low_config</code> proposal below. Step 2: approve it in{" "}
            <Link to="/approvals" style={{ color: "var(--accent, #2c7be5)" }}>
              Approvals
            </Link>
            . Step 3: run the matching action below. Do not use a{" "}
            <code>money_movement</code> proposalId. (You can also paste an
            already-approved proposalId manually.)
          </p>

          {(canReject || canSuspend) && (
            <div style={{ marginBottom: 8 }}>
              <label htmlFor="partner-reason" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
                Reason (required for reject / suspend — bound into the proposal + audit log)
              </label>
              <textarea
                id="partner-reason"
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                rows={2}
                placeholder="e.g. incomplete profile, policy violation"
                style={{ width: "100%", fontSize: 12, marginTop: 4, padding: 6 }}
              />
            </div>
          )}

          {/* Step 1 — create the proposal (browser session, no HMAC). */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {canApprove && (
              <button
                type="button"
                onClick={() => void handleCreateProposal("approve")}
                disabled={creatingProposal !== null}
                style={neutralBtnStyle(creatingProposal === null)}
              >
                {creatingProposal === "approve" ? "Creating…" : "Create approve proposal"}
              </button>
            )}
            {canReject && (
              <button
                type="button"
                onClick={() => void handleCreateProposal("reject")}
                disabled={creatingProposal !== null}
                style={neutralBtnStyle(creatingProposal === null)}
              >
                {creatingProposal === "reject" ? "Creating…" : "Create reject proposal"}
              </button>
            )}
            {canSuspend && (
              <button
                type="button"
                onClick={() => void handleCreateProposal("suspend")}
                disabled={creatingProposal !== null}
                style={neutralBtnStyle(creatingProposal === null)}
              >
                {creatingProposal === "suspend" ? "Creating…" : "Create suspend proposal"}
              </button>
            )}
          </div>

          {createProposalError && (
            <p
              role="alert"
              style={{
                margin: "0 0 8px 0",
                padding: 8,
                border: "1px solid var(--danger, #d23f3f)",
                background: "rgba(210, 63, 63, 0.06)",
                color: "var(--danger, #d23f3f)",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {createProposalError}
            </p>
          )}

          {createdProposalId && (
            <p style={{ margin: "0 0 8px 0", fontSize: 12, color: "var(--ok, #0a9444)" }}>
              Proposal created: <code>{createdProposalId}</code> ·{" "}
              <Link
                to={`/approvals/${encodeURIComponent(createdProposalId)}`}
                style={{ color: "var(--accent, #2c7be5)" }}
              >
                approve it in Approvals →
              </Link>
            </p>
          )}

          {/* Step 3 input — auto-filled by Create, or pasted manually. */}
          <input
            type="text"
            value={proposalIdInput}
            onChange={(e) => setProposalIdInput(e.target.value.trim())}
            placeholder="ulid of an APPROVED low_config proposal"
            style={{ width: "100%", padding: "6px 8px", fontFamily: "monospace", fontSize: 12 }}
          />
        </Section>
      )}

      <Section title="Actions">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canApprove && (
            <button
              type="button"
              onClick={() => {
                setDialogError(null);
                setDialog({ kind: "approve" });
              }}
              disabled={!proposalIdValid}
              title={proposalIdValid ? "" : "Paste an approved proposalId first"}
              style={primaryBtnStyle(proposalIdValid)}
            >
              Approve
            </button>
          )}
          {canReject && (
            <button
              type="button"
              onClick={() => {
                setDialogError(null);
                setDialog({ kind: "reject" });
              }}
              disabled={!proposalIdValid}
              title={proposalIdValid ? "" : "Paste an approved proposalId first"}
              style={neutralBtnStyle(proposalIdValid)}
            >
              Reject
            </button>
          )}
          {canSuspend && (
            <button
              type="button"
              onClick={() => {
                setDialogError(null);
                setDialog({ kind: "suspend" });
              }}
              disabled={!proposalIdValid}
              title={proposalIdValid ? "" : "Paste an approved proposalId first"}
              style={neutralBtnStyle(proposalIdValid)}
            >
              Suspend
            </button>
          )}
          {!anyAction && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--fg-dim)" }}>
              Row is in terminal state — view only. (A user may re-apply, which
              creates a new pending application.)
            </p>
          )}
        </div>
        {/* Reason for reject/suspend is entered in the proposalId section
            above (bound into both the proposal payload and the execute). */}
      </Section>

      <ConfirmDialog
        open={dialog.kind === "approve"}
        title="Approve partner application"
        description={
          <p style={{ margin: 0 }}>
            Sets the application to <code>approved</code> and flips{" "}
            <code>partner_approved = 1</code>, unlocking the partner-cash
            withdrawal flow for this identity. Requires an approved low_config
            proposal.
          </p>
        }
        rows={[
          { key: "applicationId", value: <code>{detail.id}</code> },
          { key: "identityId", value: <code>{detail.identityId ?? "—"}</code> },
          { key: "proposalId", value: <code>{proposalIdInput.trim()}</code> },
        ]}
        confirmLabel="Approve"
        tone="normal"
        busy={busy}
        error={dialogError}
        onConfirm={() => void runApprove()}
        onCancel={closeDialog}
      />

      <ConfirmDialog
        open={dialog.kind === "reject"}
        title="Reject partner application"
        description={
          <p style={{ margin: 0 }}>
            Records operator-rejection. <code>partner_approved</code> is left
            untouched. The reason is persisted in the BE audit log.
          </p>
        }
        rows={[
          { key: "applicationId", value: <code>{detail.id}</code> },
          { key: "proposalId", value: <code>{proposalIdInput.trim()}</code> },
          {
            key: "reason",
            value: reasonValid ? (
              <code style={{ whiteSpace: "pre-wrap" }}>{reasonInput.trim()}</code>
            ) : (
              <em style={{ color: "var(--danger, #d23f3f)" }}>required</em>
            ),
          },
        ]}
        confirmLabel="Reject"
        tone="warning"
        busy={busy}
        confirmDisabled={!reasonValid}
        error={dialogError}
        onConfirm={() => void runReject()}
        onCancel={closeDialog}
      />

      <ConfirmDialog
        open={dialog.kind === "suspend"}
        title="Suspend partner"
        description={
          <p style={{ margin: 0 }}>
            Sets the application to <code>suspended</code> and clears{" "}
            <code>partner_approved = 0</code>, blocking new partner-cash
            withdrawals. Existing queued withdrawals are not auto-resolved —
            handle them via the withdrawals queue.
          </p>
        }
        rows={[
          { key: "applicationId", value: <code>{detail.id}</code> },
          { key: "identityId", value: <code>{detail.identityId ?? "—"}</code> },
          { key: "proposalId", value: <code>{proposalIdInput.trim()}</code> },
          {
            key: "reason",
            value: reasonValid ? (
              <code style={{ whiteSpace: "pre-wrap" }}>{reasonInput.trim()}</code>
            ) : (
              <em style={{ color: "var(--danger, #d23f3f)" }}>required</em>
            ),
          },
        ]}
        confirmLabel="Suspend"
        tone="warning"
        busy={busy}
        confirmDisabled={!reasonValid}
        error={dialogError}
        onConfirm={() => void runSuspend()}
        onCancel={closeDialog}
      />
    </div>
  );
}

export function PartnerApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  return (
    <div>
      <PageHeader
        title={`Partner application — ${id ?? ""}`}
        subtitle="Approve · Reject · Suspend. All three are low_config and require an APPROVED multisign proposalId pasted below."
        method="GET / POST"
        path="/v2/admin/bff/affiliate/partners/applications/:id"
      />
      <AuthRequired>
        {id ? <PartnerApplicationDetailInner id={id} /> : <ErrorBox text="Missing :id in URL" />}
      </AuthRequired>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "14px 16px",
        margin: "16px 0",
        background: "var(--bg-card)",
      }}
    >
      <h3 style={{ margin: "0 0 12px 0", fontSize: 14 }}>{title}</h3>
      {children}
    </section>
  );
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "6px 8px", color: "var(--fg-dim)", width: 200 }}>{k}</td>
      <td style={{ padding: "6px 8px", wordBreak: "break-all" }}>{v}</td>
    </tr>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: 12,
        border: "1px solid var(--danger, #d23f3f)",
        background: "rgba(210, 63, 63, 0.06)",
        color: "var(--danger, #d23f3f)",
        borderRadius: 4,
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

function primaryBtnStyle(enabled: boolean) {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "var(--accent, #2c7be5)",
    color: "white",
    border: "none",
    borderRadius: 3,
    cursor: enabled ? ("pointer" as const) : ("default" as const),
    opacity: enabled ? 1 : 0.5,
    fontWeight: 600,
  };
}

function neutralBtnStyle(enabled: boolean) {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "transparent",
    color: "var(--danger, #d23f3f)",
    border: "1px solid var(--danger, #d23f3f)",
    borderRadius: 3,
    cursor: enabled ? ("pointer" as const) : ("default" as const),
    opacity: enabled ? 1 : 0.5,
    fontWeight: 600,
  };
}
